import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

// ═══════════════════════════════════════════════════════════════
// Anagogia Theme Provider (CSS-only)
// Manages two orthogonal dimensions:
//   1. Mode (light/dark) — controls CSS tokens via data-theme attribute
//   2. Content Theme (editorial/manuscript/cabinet) — controls reading fonts/colors
// ═══════════════════════════════════════════════════════════════

export type ThemeMode = 'light' | 'dark';
export type ModePreference = 'light' | 'dark' | 'system';
export type ContentTheme = 'editorial' | 'manuscript' | 'cabinet';

interface ThemeContextType {
  mode: ThemeMode;
  modePreference: ModePreference;
  contentTheme: ContentTheme;
  colorblindMode: boolean;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
  setModePreference: (pref: ModePreference) => void;
  setContentTheme: (theme: ContentTheme) => void;
  setColorblindMode: (enabled: boolean) => void;
}

const STORAGE_KEYS = {
  MODE: 'ag-theme-mode',
  CONTENT_THEME: 'ag-content-theme',
  COLORBLIND: 'ag-colorblind-mode',
} as const;

const ThemeContext = createContext<ThemeContextType | null>(null);

function getSystemMode(): ThemeMode {
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

function getInitialModePreference(): ModePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.MODE);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // localStorage not available
  }
  return 'system';
}

function getInitialMode(): ThemeMode {
  const pref = getInitialModePreference();
  if (pref === 'light' || pref === 'dark') return pref;
  return getSystemMode();
}

function getInitialColorblindMode(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEYS.COLORBLIND) === 'true';
  } catch {
    return false;
  }
}

function getInitialContentTheme(): ContentTheme {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CONTENT_THEME);
    if (stored === 'editorial' || stored === 'manuscript' || stored === 'cabinet') return stored;
  } catch {
    // localStorage not available
  }
  return 'editorial';
}

interface AnagogiaThemeProviderProps {
  children: React.ReactNode;
}

export function AnagogiaThemeProvider({ children }: AnagogiaThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(getInitialMode);
  const [modePreference, setModePreferenceState] = useState<ModePreference>(getInitialModePreference);
  const [contentTheme, setContentThemeState] = useState<ContentTheme>(getInitialContentTheme);
  const [colorblindMode, setColorblindModeState] = useState<boolean>(getInitialColorblindMode);

  // Apply data attributes to <html> for CSS variable cascading
  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('data-theme', mode);
    html.setAttribute('data-content-theme', contentTheme);
  }, [mode, contentTheme]);

  // Persist preferences to localStorage
  useEffect(() => {
    try {
      if (modePreference === 'system') {
        localStorage.removeItem(STORAGE_KEYS.MODE);
      } else {
        localStorage.setItem(STORAGE_KEYS.MODE, modePreference);
      }
    } catch {
      // Silently fail
    }
  }, [modePreference]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.CONTENT_THEME, contentTheme);
    } catch {
      // Silently fail
    }
  }, [contentTheme]);

  // Listen to system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      // Only auto-switch if user is in system mode
      if (modePreference === 'system') {
        setModeState(e.matches ? 'dark' : 'light');
      }
    };
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [modePreference]);

  const toggleMode = useCallback(() => {
    setModeState((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      setModePreferenceState(next);
      return next;
    });
  }, []);

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    setModePreferenceState(newMode);
  }, []);

  const setModePreference = useCallback((pref: ModePreference) => {
    setModePreferenceState(pref);
    if (pref === 'system') {
      setModeState(getSystemMode());
    } else {
      setModeState(pref);
    }
  }, []);

  const setContentTheme = useCallback((theme: ContentTheme) => {
    setContentThemeState(theme);
  }, []);

  const setColorblindMode = useCallback((enabled: boolean) => {
    setColorblindModeState(enabled);
    try {
      if (enabled) {
        localStorage.setItem(STORAGE_KEYS.COLORBLIND, 'true');
      } else {
        localStorage.removeItem(STORAGE_KEYS.COLORBLIND);
      }
    } catch {
      // Silently fail
    }
  }, []);

  const contextValue = useMemo<ThemeContextType>(
    () => ({ mode, modePreference, contentTheme, colorblindMode, toggleMode, setMode, setModePreference, setContentTheme, setColorblindMode }),
    [mode, modePreference, contentTheme, colorblindMode, toggleMode, setMode, setModePreference, setContentTheme, setColorblindMode],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access the Anagogia theme context.
 * Must be used within an AnagogiaThemeProvider.
 */
export function useAnagogiaTheme(): ThemeContextType {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useAnagogiaTheme must be used within an AnagogiaThemeProvider');
  }
  return ctx;
}
